import type { ComponentType } from 'react'
import type { useLandingStart } from '../screens/landing.tsx'

/**
 * Every hero `/` has shown or may show, oldest first. A version is a file in `versions/`
 * exporting `Hero`; once it has met real traffic it is frozen (registry.test.ts checks the file's
 * hash), so a test result always belongs to the markup that produced it. Changing a frozen hero
 * means a new version. Workflow: docs/HERO-AB.md.
 */

export type HeroProps = ReturnType<typeof useLandingStart>
export type HeroModule = { Hero: ComponentType<HeroProps> }

export type HeroVersion = {
  /** Short and stable: it goes into `?v=`, `leads.variant` and PostHog. */
  id: string
  name: string
  description: string
  /** File in `versions/`. */
  file: string
  /** Figma node in file 8ORxH44yttxtlMwqfn6RBR. */
  figma?: string
  /** When it was the default on `/` outside tests (Europe/Warsaw dates, `to` inclusive). */
  live?: ReadonlyArray<{ from: string; to?: string }>
  /** sha256 of the file once frozen (`pnpm hero:freeze <id>`). */
  frozen?: string
}

const MODULES = import.meta.glob<HeroModule>('./versions/*.tsx')

export const HERO_VERSIONS = [
  {
    id: 'h1',
    name: 'Stare hero (wideo)',
    description:
      'Pierwsze hero pilota: „Mów po angielsku od pierwszej lekcji”, karta „Dobierzemy plan i lektora”, wideo w panelu na desktopie.',
    file: 'h1-stare.tsx',
    figma: '132:2416',
    live: [{ from: '2026-10-09', to: '2026-10-23' }],
    frozen: 'c6df99c8098e71e0743b87d85828646480fe6dab4faf5f9e376155f8a9aad4ee',
  },
  {
    id: 'h2',
    name: 'Hook New',
    description:
      '„Zacznij mówić po angielsku nawet od zera”, kafelek z najbliższym terminem, 4 kroki, czat pod spodem, przyklejony przycisk na telefonie.',
    file: 'h2-nowe.tsx',
    figma: '863:1172',
    live: [{ from: '2026-10-23' }],
    frozen: 'a0dc94ef10874b4175d0bdaec3f5119aa8d2cf79e5bb9e1d08e656612fc80a05',
  },
  {
    id: 'h3',
    name: 'Wycena (Hook 3)',
    description:
      'Kopia live leadhub /q/indywidualna-wycena: „Uzyskaj wycenę kursu”, „Zaczynamy” i czat pod przyciskiem, bez terminu i kroków.',
    file: 'h3-wycena.tsx',
    figma: '1102:2287',
    frozen: 'd68a208d849f9ea8c35f30142e8fb85247d0e5efaea2f4b4f2ef892a1e4da942',
  },
  // pnpm hero:new appends new versions above this line
] as const satisfies ReadonlyArray<HeroVersion>

export type HeroVersionId = (typeof HERO_VERSIONS)[number]['id']

export function heroVersion(id: string): HeroVersion | undefined {
  return HERO_VERSIONS.find((v) => v.id === id)
}

export function loadHero(version: HeroVersion): Promise<HeroModule> {
  const load = MODULES[`./versions/${version.file}`]
  if (!load) throw new Error(`Hero file missing: versions/${version.file}`)
  return load()
}
